import * as ss from 'superstruct';
import { fromEnum } from '../utils/from-enum';
import { JsonValue } from '../utils/json';
import { BrandAssetId, BrandAssetKind } from './brand-asset';
import { NonBlankString } from './non-blank-string';

export enum BrandKitFieldType {
  Text = 'text',
  LongText = 'longtext',
  Color = 'color',
  Url = 'url',
  List = 'list',
  Font = 'font',
  /** An image from the brand asset library, by asset id. */
  Image = 'image',
}

export enum BrandKitListItemType {
  Text = 'text',
  Color = 'color',
  Url = 'url',
  /** Asset ids of images. */
  Image = 'image',
}

export const BrandKitLimits = {
  fields: 100,
  keyLength: 64,
  labelLength: 100,
  descriptionLength: 1000,
  textLength: 500,
  longTextLength: 5000,
  listItems: 50,
  listItemLength: 500,
  urlLength: 2048,
  /** Total JSON size of all fields, keeping kits within request limits and AI context. */
  totalChars: 50000,
} as const;

export const BrandKitFieldKeyPattern = /^[a-z][a-z0-9_]*$/;

/** 3 or 6 digits only, matching what color pickers accept. */
export const HexColor = ss.pattern(
  ss.string(),
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
);

/** An http(s) URL with a hostname. */
export const HttpUrl = ss.size(
  ss.pattern(
    ss.string(),
    /^https?:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)*(?::\d{1,5})?(?:[/?#]\S*)?$/i,
  ),
  1,
  BrandKitLimits.urlLength,
);

const fieldBase = {
  key: ss.size(
    ss.pattern(ss.string(), BrandKitFieldKeyPattern),
    1,
    BrandKitLimits.keyLength,
  ),
  label: NonBlankString(BrandKitLimits.labelLength),
  description: ss.size(ss.string(), 0, BrandKitLimits.descriptionLength),
};

export const BrandKitTextField = ss.object({
  ...fieldBase,
  type: ss.literal(BrandKitFieldType.Text),
  value: ss.size(ss.string(), 0, BrandKitLimits.textLength),
});

export type BrandKitTextField = ss.Infer<typeof BrandKitTextField>;

export const BrandKitLongTextField = ss.object({
  ...fieldBase,
  type: ss.literal(BrandKitFieldType.LongText),
  value: ss.size(ss.string(), 0, BrandKitLimits.longTextLength),
});

export type BrandKitLongTextField = ss.Infer<typeof BrandKitLongTextField>;

export const BrandKitColorField = ss.object({
  ...fieldBase,
  type: ss.literal(BrandKitFieldType.Color),
  value: ss.optional(HexColor),
});

export type BrandKitColorField = ss.Infer<typeof BrandKitColorField>;

export const BrandKitUrlField = ss.object({
  ...fieldBase,
  type: ss.literal(BrandKitFieldType.Url),
  value: ss.optional(HttpUrl),
});

export type BrandKitUrlField = ss.Infer<typeof BrandKitUrlField>;

export const BrandKitImageField = ss.object({
  ...fieldBase,
  type: ss.literal(BrandKitFieldType.Image),
  value: ss.optional(BrandAssetId),
});

export type BrandKitImageField = ss.Infer<typeof BrandKitImageField>;

const listItemStructs: Record<BrandKitListItemType, ss.Struct<string, null>> = {
  [BrandKitListItemType.Text]: NonBlankString(BrandKitLimits.listItemLength),
  [BrandKitListItemType.Color]: HexColor,
  [BrandKitListItemType.Url]: HttpUrl,
  [BrandKitListItemType.Image]: BrandAssetId,
};

export const isValidListItem = (
  itemType: BrandKitListItemType,
  item: string,
): boolean => ss.is(item, listItemStructs[itemType]);

export const BrandKitListField = ss.refine(
  ss.object({
    ...fieldBase,
    type: ss.literal(BrandKitFieldType.List),
    itemType: fromEnum(BrandKitListItemType),
    value: ss.size(ss.array(ss.string()), 0, BrandKitLimits.listItems),
  }),
  'BrandKitListItems',
  (field) =>
    field.value.every((item) => isValidListItem(field.itemType, item)) ||
    `Every item must be a valid ${field.itemType}`,
);

export type BrandKitListField = ss.Infer<typeof BrandKitListField>;

export const BrandKitFont = ss.object({
  family: NonBlankString(BrandKitLimits.labelLength),
  /** Stylesheet that loads the font, e.g. a Google Fonts css2 URL. */
  cssUrl: ss.optional(HttpUrl),
  /** CSS generic family or stack to fall back to, e.g. `sans-serif`. */
  fallback: ss.optional(NonBlankString(BrandKitLimits.labelLength)),
  /** An uploaded font file from the brand asset library. */
  assetId: ss.optional(BrandAssetId),
});

export type BrandKitFont = ss.Infer<typeof BrandKitFont>;

export const BrandKitFontField = ss.object({
  ...fieldBase,
  type: ss.literal(BrandKitFieldType.Font),
  value: ss.optional(BrandKitFont),
});

export type BrandKitFontField = ss.Infer<typeof BrandKitFontField>;

export const BrandKitField = ss.union([
  BrandKitTextField,
  BrandKitLongTextField,
  BrandKitColorField,
  BrandKitUrlField,
  BrandKitListField,
  BrandKitFontField,
  BrandKitImageField,
]);

export type BrandKitField = ss.Infer<typeof BrandKitField>;

export const BrandKitFields = ss.refine(
  ss.size(ss.array(BrandKitField), 0, BrandKitLimits.fields),
  'BrandKitFields',
  (fields) => {
    const keys = new Set(fields.map((f) => f.key));

    if (keys.size !== fields.length) {
      return 'Field keys must be unique';
    }

    if (JSON.stringify(fields).length > BrandKitLimits.totalChars) {
      return `Brand kit exceeds ${BrandKitLimits.totalChars} characters`;
    }

    return true;
  },
);

/** A field with no type-specific settings or value; the type is picked separately. */
export type BrandKitFieldHeader = Pick<
  BrandKitField,
  'key' | 'label' | 'description'
>;

/** A new field of `type` with an empty value. */
export const emptyBrandKitField = (
  header: BrandKitFieldHeader,
  type: BrandKitFieldType,
  itemType: BrandKitListItemType = BrandKitListItemType.Text,
): BrandKitField => {
  switch (type) {
    case BrandKitFieldType.Text:
    case BrandKitFieldType.LongText:
      return { ...header, type, value: '' };
    case BrandKitFieldType.Color:
    case BrandKitFieldType.Url:
    case BrandKitFieldType.Font:
    case BrandKitFieldType.Image:
      return { ...header, type };
    case BrandKitFieldType.List:
      return { ...header, type, itemType, value: [] };
  }
};

/** True when the field has nothing filled in. */
export const isBrandKitFieldEmpty = (field: BrandKitField): boolean => {
  switch (field.type) {
    case BrandKitFieldType.List:
      return field.value.length === 0;
    case BrandKitFieldType.Font:
      return !field.value?.family.trim();
    default:
      return !field.value?.trim();
  }
};

/** Why `fields` can't be saved or published, if some have no value. */
export const unsetBrandKitValuesMessage = (
  fields: readonly BrandKitField[],
): string | undefined => {
  const unset = fields.flatMap((f) =>
    isBrandKitFieldEmpty(f) ? [f.label] : [],
  );

  return unset.length > 0
    ? `Every field needs a value. Fill in or remove: ${unset.join(', ')}`
    : undefined;
};

/**
 * Fields that all have values, as required to save or publish a draft. Stored
 * versions may still have empty fields (e.g. the default template).
 */
export const BrandKitCompleteFields = ss.refine(
  BrandKitFields,
  'BrandKitCompleteFields',
  (fields) => unsetBrandKitValuesMessage(fields) ?? true,
);

const fieldStructs: Readonly<Record<BrandKitFieldType, ss.Struct<any, any>>> = {
  [BrandKitFieldType.Text]: BrandKitTextField,
  [BrandKitFieldType.LongText]: BrandKitLongTextField,
  [BrandKitFieldType.Color]: BrandKitColorField,
  [BrandKitFieldType.Url]: BrandKitUrlField,
  [BrandKitFieldType.List]: BrandKitListField,
  [BrandKitFieldType.Font]: BrandKitFontField,
  [BrandKitFieldType.Image]: BrandKitImageField,
};

const FieldWithType = ss.type({ type: fromEnum(BrandKitFieldType) });
const FieldWithKey = ss.type({ key: ss.string() });

/** Why `field` isn't valid for its own type, or undefined if it is. */
const brandKitFieldError = (
  field: ss.Infer<typeof FieldWithType>,
): string | undefined => {
  const [error] = ss.validate(field, fieldStructs[field.type]);

  if (!error) {
    return undefined;
  }

  const [failure] = error.failures();

  return failure.path.length > 0
    ? `${failure.path.join('.')}: ${failure.message}`
    : failure.message;
};

/**
 * Why `value` can't be saved as a draft's fields, naming the field at fault,
 * or undefined if it can. Clearer than a StructError, which can't tell which
 * type a field was meant to be.
 */
export const brandKitFieldsError = (value: JsonValue): string | undefined => {
  const [error] = ss.validate(value, BrandKitCompleteFields);

  if (!error) {
    return undefined;
  }

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const field = value[i];

      const fieldError = ss.is(field, FieldWithType)
        ? brandKitFieldError(field)
        : `type must be one of: ${Object.values(BrandKitFieldType).join(', ')}`;

      if (fieldError) {
        const key = ss.is(field, FieldWithKey) ? ` ("${field.key}")` : '';

        return `Field ${i + 1}${key}: ${fieldError}`;
      }
    }
  }

  return error.message;
};

/** True for fields whose value is image asset ids. */
const holdsImages = (field: BrandKitField): boolean =>
  field.type === BrandKitFieldType.Image ||
  (field.type === BrandKitFieldType.List &&
    field.itemType === BrandKitListItemType.Image);

export interface BrandKitAssetRef {
  assetId: string;
  /** The kind of asset the referencing field needs. */
  kind: BrandAssetKind;
}

/** Every asset `fields` use, once per id and kind. */
export const brandKitAssetRefs = (
  fields: readonly BrandKitField[],
): BrandKitAssetRef[] => {
  const refs = new Map<string, BrandKitAssetRef>();

  const add = (assetId: string, kind: BrandAssetKind) =>
    refs.set(`${kind}:${assetId}`, { assetId, kind });

  for (const field of fields) {
    if (field.type === BrandKitFieldType.Font && field.value?.assetId) {
      add(field.value.assetId, BrandAssetKind.Font);
    } else if (field.type === BrandKitFieldType.Image && field.value) {
      add(field.value, BrandAssetKind.Image);
    } else if (
      field.type === BrandKitFieldType.List &&
      field.itemType === BrandKitListItemType.Image
    ) {
      field.value.forEach((id) => add(id, BrandAssetKind.Image));
    }
  }

  return [...refs.values()];
};

/** The ids of every asset `fields` use. */
export const brandKitAssetIds = (
  fields: readonly BrandKitField[],
): string[] => [
  ...new Set(brandKitAssetRefs(fields).map((ref) => ref.assetId)),
];

/** A field whose value may not suit its type until it's checked. */
type BrandKitFieldCandidate = Omit<BrandKitField, 'value'> & {
  value?: BrandKitField['value'];
};

export interface BrandKitFieldTypeChange {
  field: BrandKitField;
  /** True when some of the old value couldn't be carried over. */
  lossy: boolean;
}

/**
 * Converts `field` to `type`, keeping as much of its value as is valid for the
 * new type.
 */
export const changeBrandKitFieldType = (
  field: BrandKitField,
  type: BrandKitFieldType,
  itemType: BrandKitListItemType = BrandKitListItemType.Text,
): BrandKitFieldTypeChange => {
  if (field.type === type && type !== BrandKitFieldType.List) {
    return { field, lossy: false };
  }

  const header = {
    key: field.key,
    label: field.label,
    description: field.description,
  };

  const empty = emptyBrandKitField(header, type, itemType);

  // Asset ids mean nothing as text, and text can't name an asset.
  if (holdsImages(field) !== holdsImages(empty)) {
    return { field: empty, lossy: !isBrandKitFieldEmpty(field) };
  }

  const candidates: BrandKitFieldCandidate[] = [];
  let dropped = false;

  if (field.type === BrandKitFieldType.List) {
    if (empty.type === BrandKitFieldType.List) {
      const kept = field.value.filter((item) =>
        isValidListItem(empty.itemType, item),
      );

      dropped = kept.length < field.value.length;
      candidates.push({ ...empty, value: kept });
    } else if (field.value.length === 1) {
      candidates.push({ ...empty, value: field.value[0] });
    } else if (
      empty.type === BrandKitFieldType.Text ||
      empty.type === BrandKitFieldType.LongText
    ) {
      candidates.push({ ...empty, value: field.value.join('\n') });
    }
  } else if (field.type === BrandKitFieldType.Font) {
    if (field.value && empty.type !== BrandKitFieldType.List) {
      const { cssUrl, fallback, assetId } = field.value;
      dropped = !!(cssUrl || fallback || assetId);
      candidates.push({ ...empty, value: field.value.family });
    }
  } else if (field.value) {
    if (empty.type === BrandKitFieldType.List) {
      candidates.push({ ...empty, value: [field.value] });
    } else if (empty.type === BrandKitFieldType.Font) {
      candidates.push({ ...empty, value: { family: field.value } });
    } else {
      candidates.push({ ...empty, value: field.value });
    }
  }

  const converted = candidates.find((c) => ss.is(c, BrandKitField)) ?? empty;

  return {
    field: converted,
    lossy:
      dropped ||
      (!isBrandKitFieldEmpty(field) && isBrandKitFieldEmpty(converted)),
  };
};

/**
 * A snake_case key for `label` that is not in `existingKeys`, e.g.
 * "Gray Palette" -> `gray_palette`, or `gray_palette_2` when taken.
 */
export const brandKitKeyFromLabel = (
  label: string,
  existingKeys: Iterable<string>,
): string => {
  const taken = new Set(existingKeys);

  let base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  if (!base) {
    base = 'field';
  } else if (!/^[a-z]/.test(base)) {
    base = `f_${base}`;
  }

  const maxBase = BrandKitLimits.keyLength - 4; // Room for a `_NNN` suffix.
  base = base.slice(0, maxBase).replace(/_$/, '');

  let key = base;

  for (let n = 2; taken.has(key); n++) {
    key = `${base}_${n}`;
  }

  return key;
};
